import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Observable, concat, of, timer } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthenticatedLoadingCoordinatorService, AuthenticatedLoadingState } from '@core/services/infra/authenticated-loading-coordinator.service';
import { BrandSplashComponent } from '@shared/components/brand-splash/brand-splash.component';
import { WorkspaceSwitchCurtainComponent } from '@shared/components/workspace-switch-curtain/workspace-switch-curtain.component';

type DisplayState = AuthenticatedLoadingState & { fading?: boolean };

@Component({
    selector: 'app-authenticated-loading-screen',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        TranslatePipe,
        BrandSplashComponent,
        WorkspaceSwitchCurtainComponent
    ],
    template: `
    @if (displayState$ | async; as state) {
      <div class="authenticated-loading-overlay"
        [class.fade-out]="!!state.fading || !state.isLoading"
        [class.mode-tour]="state.uiMode === 'tour'"
        [class.mode-workspace-switch]="state.uiMode === 'workspace-switch'"
        [class.mode-minimal]="state.uiMode !== 'tour' && state.uiMode !== 'workspace-switch'">
        @if (state.uiMode === 'tour') {
          <!-- Guided tour — escalates after 2 s of continuous loading. -->
          <div class="blob blob-a" aria-hidden="true"></div>
          <div class="blob blob-b" aria-hidden="true"></div>

          <div class="tour-layout">
            <div class="tour-rail">
              <!-- The glyph variant, because the rail sets the word beside it.
                   It also carries the on-gradient accent (#E4D3FF, §14.3) that
                   logo-glyph-white.svg — an on-DARK asset — never had here. -->
              <div class="tour-brand">
                <app-brand-splash
                  class="tour-brand__mark"
                  variant="glyph"
                  [phase]="state.markPhase ?? 'continue'" />
                <span class="brand-wordmark">Proporio</span>
              </div>
              <div class="tour-rail-bottom">
                <div class="tour-kicker">{{ 'shared.authenticatedLoadingScreen.tourKicker' | translate }}</div>
                <h2 class="tour-title">{{ 'shared.authenticatedLoadingScreen.tourTitle' | translate }}</h2>
                <p class="tour-intro">{{ 'shared.authenticatedLoadingScreen.tourIntro' | translate }}</p>
                <div class="tour-track"
                  role="progressbar"
                  [attr.aria-label]="'shared.authenticatedLoadingScreen.tourStatus' | translate"
                  [attr.aria-valuenow]="Math.round(state.progress)"
                  aria-valuemin="0"
                  aria-valuemax="100">
                  <div class="tour-fill" [style.width.%]="state.progress"></div>
                </div>
                <div class="tour-status">
                  <span class="mini-spinner" aria-hidden="true"></span>
                  <span role="status">{{ 'shared.authenticatedLoadingScreen.tourStatus' | translate }}</span>
                  <span aria-hidden="true">· {{ Math.round(state.progress) }}%</span>
                </div>
              </div>
            </div>

            <div class="tour-stage" aria-hidden="true">
              <div class="tour-cards">

                <div class="tour-card" style="animation-delay:0s">
                  <div class="card">
                    <div class="card-head">
                      <span class="card-title">{{ 'shared.authenticatedLoadingScreen.cardCockpitTitle' | translate }}</span>
                      <span class="badge">{{ 'shared.authenticatedLoadingScreen.cardCockpitBadge' | translate }}</span>
                    </div>
                    <div class="task"><span class="chk chk-on"></span><div class="sk"><i style="width:70%"></i><i class="lo" style="width:40%"></i></div></div>
                    <div class="task"><span class="chk"></span><div class="sk"><i style="width:85%"></i><i class="lo" style="width:52%"></i></div></div>
                    <div class="task"><span class="chk"></span><div class="sk"><i style="width:60%"></i><i class="lo" style="width:35%"></i></div></div>
                    <span class="sample-tag">{{ 'shared.authenticatedLoadingScreen.cardSampleLabel' | translate }}</span>
                  </div>
                  <div class="caption">{{ 'shared.authenticatedLoadingScreen.cardCockpit' | translate }}</div>
                </div>

                <div class="tour-card" style="animation-delay:2.5s">
                  <div class="card">
                    <div class="card-title">{{ 'shared.authenticatedLoadingScreen.cardTresorerieTitle' | translate }}</div>
                    <div class="money">{{ 'shared.authenticatedLoadingScreen.cardTresorerieAmount' | translate }}<span>{{ 'shared.authenticatedLoadingScreen.cardTresorerieMonth' | translate }}</span></div>
                    <div class="chart">
                      <div class="bar" style="height:52%;animation-delay:0s"></div>
                      <div class="bar" style="height:70%;animation-delay:.2s"></div>
                      <div class="bar" style="height:44%;animation-delay:.4s"></div>
                      <div class="bar hi" style="height:88%;animation-delay:.6s"></div>
                      <div class="bar" style="height:64%;animation-delay:.8s"></div>
                      <div class="bar hi" style="height:100%;animation-delay:1s"></div>
                    </div>
                    <span class="sample-tag">{{ 'shared.authenticatedLoadingScreen.cardSampleLabel' | translate }}</span>
                  </div>
                  <div class="caption">{{ 'shared.authenticatedLoadingScreen.cardTresorerie' | translate }}</div>
                </div>

                <div class="tour-card" style="animation-delay:5s">
                  <div class="card">
                    <div class="card-title mb">{{ 'shared.authenticatedLoadingScreen.cardBiensTitle' | translate }}</div>
                    <div class="stats">
                      <div class="stat"><b style="color:#6E4BF0">12</b><span>{{ 'shared.authenticatedLoadingScreen.cardBiensStatProperties' | translate }}</span></div>
                      <div class="stat"><b style="color:#9A4BE8">94%</b><span>{{ 'shared.authenticatedLoadingScreen.cardBiensStatOccupancy' | translate }}</span></div>
                      <div class="stat"><b style="color:#16a34a">28</b><span>{{ 'shared.authenticatedLoadingScreen.cardBiensStatTenants' | translate }}</span></div>
                      <div class="stat"><b style="color:#f59e0b">3</b><span>{{ 'shared.authenticatedLoadingScreen.cardBiensStatLeases' | translate }}</span></div>
                    </div>
                    <span class="sample-tag">{{ 'shared.authenticatedLoadingScreen.cardSampleLabel' | translate }}</span>
                  </div>
                  <div class="caption">{{ 'shared.authenticatedLoadingScreen.cardBiens' | translate }}</div>
                </div>

                <div class="tour-card" style="animation-delay:7.5s">
                  <div class="card">
                    <div class="card-title mb">{{ 'shared.authenticatedLoadingScreen.cardLocatairesTitle' | translate }}</div>
                    <div class="tenant"><span class="avatar" style="background:linear-gradient(135deg,#6E4BF0,#9A4BE8)">CM</span><div><b>Camille Martin</b><i class="ok">{{ 'shared.authenticatedLoadingScreen.cardTenantUpToDate' | translate }}</i></div></div>
                    <div class="tenant"><span class="avatar" style="background:linear-gradient(135deg,#7C3BEE,#B79BFF)">LD</span><div><b>Lucas Dubois</b><i class="ok">{{ 'shared.authenticatedLoadingScreen.cardTenantUpToDate' | translate }}</i></div></div>
                    <div class="tenant"><span class="avatar" style="background:linear-gradient(135deg,#9A4BE8,#6E4BF0)">AR</span><div><b>Amina Rossi</b><i class="warn">{{ 'shared.authenticatedLoadingScreen.cardTenantToSign' | translate }}</i></div></div>
                    <span class="sample-tag">{{ 'shared.authenticatedLoadingScreen.cardSampleLabel' | translate }}</span>
                  </div>
                  <div class="caption">{{ 'shared.authenticatedLoadingScreen.cardLocataires' | translate }}</div>
                </div>

              </div>
            </div>
          </div>

          <div class="powered-by"><span>Proporio</span></div>
        } @else if (state.uiMode === 'workspace-switch' && state.workspaceSwitch) {
          <app-workspace-switch-curtain
            [marker]="state.workspaceSwitch"
            [progress]="state.showDetailedProgress && state.progress > 0 ? state.progress : null" />
        } @else {
          <!-- Minimal curtain (&lt; 2 s). Matches the HTML #vex-splash-screen
               geometry so the logo does not blink at handover. Signup and
               login share this surface until the tour escalates. -->
          <app-brand-splash
            [phase]="state.markPhase ?? 'continue'"
            [message]="state.message | translate: (state.messageParams || {})"
            [progress]="state.showDetailedProgress && state.progress > 0 ? state.progress : null"
            [tagline]="'shared.brandSplash.tagline' | translate" />
        }
      </div>
    }
    `,
    styles: [`
    .authenticated-loading-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(150deg, #6E4BF0 0%, #7C3BEE 52%, #9A4BE8 100%);
      background-size: 200% 200%;
      animation: gradientShift 16s ease infinite;
      display: flex;
      align-items: center;
      justify-content: center;
      /* ABOVE #vex-splash-screen, which sits at 99999 in src/index.html.
         The splash fades out over 400 ms while this curtain is already up; from
         underneath it that fade is invisible, whereas from on top it showed the
         two status lines overlapping and both unreadable. The marks coincide to
         the pixel, so the only thing the handover has left to hide is the text. */
      z-index: 100000;
      opacity: 1;
    }

    .authenticated-loading-overlay.mode-tour {
      align-items: stretch;
      justify-content: flex-start;
      overflow: hidden;
    }

    .authenticated-loading-overlay.mode-minimal,
    .authenticated-loading-overlay.mode-workspace-switch {
      animation: none;
      background: linear-gradient(150deg, #6E4BF0 0%, #7C3BEE 52%, #9A4BE8 100%);
    }

    @keyframes gradientShift {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }

    .authenticated-loading-overlay.fade-out {
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.4s ease-out;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    /* ───────────────────────── Guided tour ───────────────────────── */

    .blob {
      position: absolute;
      border-radius: 50%;
      pointer-events: none;
    }

    .blob-a {
      width: 600px;
      height: 600px;
      top: -200px;
      right: -160px;
      filter: blur(36px);
      background: radial-gradient(circle, rgba(132, 96, 246, 0.42), transparent 70%);
      animation: floatB 17s ease-in-out infinite;
    }

    .blob-b {
      width: 460px;
      height: 460px;
      bottom: -160px;
      left: -120px;
      filter: blur(30px);
      background: radial-gradient(circle, rgba(67, 128, 246, 0.4), transparent 70%);
      animation: floatC 21s ease-in-out infinite;
    }

    @keyframes floatB {
      0%, 100% { transform: translate(0, 0); }
      50% { transform: translate(-38px, 30px); }
    }

    @keyframes floatC {
      0%, 100% { transform: translate(0, 0); }
      50% { transform: translate(24px, 26px); }
    }

    .tour-layout {
      position: relative;
      z-index: 2;
      display: flex;
      width: 100%;
      height: 100%;
      color: #fff;
    }

    .tour-rail {
      width: 400px;
      flex: 0 0 400px;
      padding: 60px 52px;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      text-align: left;
    }

    /* A lockup, so the gap follows the charter's ratio (0.567 of the symbol's
       ink height) and the weight comes from the .brand-wordmark class. */
    .tour-brand {
      display: flex;
      align-items: center;
      gap: 19px;
      font-size: 19px;
    }

    .tour-rail-bottom { margin-top: auto; }

    .tour-kicker {
      font-size: 13px;
      font-weight: 700;
      letter-spacing: 0.16em;
      color: rgba(255, 255, 255, 0.6);
      margin-bottom: 14px;
    }

    .tour-title {
      margin: 0 0 14px;
      font-size: 32px;
      font-weight: 800;
      line-height: 1.15;
      letter-spacing: -0.02em;
    }

    .tour-intro {
      margin: 0 0 34px;
      font-size: 15px;
      line-height: 1.55;
      color: rgba(255, 255, 255, 0.72);
    }

    .tour-track {
      height: 5px;
      border-radius: 4px;
      background: rgba(255, 255, 255, 0.2);
      overflow: hidden;
      margin-bottom: 20px;
    }

    .tour-fill {
      height: 100%;
      background: #fff;
      border-radius: 4px;
      transition: width 0.3s ease;
    }

    .tour-status {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 13.5px;
      color: rgba(255, 255, 255, 0.72);
    }

    .tour-status .mini-spinner { margin-right: 4px; }

    .mini-spinner {
      width: 16px;
      height: 16px;
      flex: 0 0 auto;
      border: 2px solid rgba(255, 255, 255, 0.35);
      border-top-color: #fff;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    .tour-stage {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      padding-right: 40px;
    }

    /* Tenant workspace: drop the manager carousel.
       The cards show a cockpit, a cashflow figure and a list of tenants — three
       things a tenant will never have. Showing them during the wait promises a
       product that is not theirs. The rail alone carries the wait, centred.
       Scoped on the theme attribute the router already sets, so no new input
       and no workspace lookup inside a shared component. */
    :host-context([data-theme='tenant']) .tour-stage {
      display: none;
    }

    :host-context([data-theme='tenant']) .tour-rail {
      width: 100%;
      flex: 1 1 auto;
      align-items: center;
      text-align: center;
      padding: 60px 32px;
    }

    :host-context([data-theme='tenant']) .tour-rail-bottom {
      max-width: 460px;
    }

    .tour-cards {
      position: relative;
      width: 440px;
      height: 420px;
    }

    /* 10s cycle, not 20s: the coordinator finishes as soon as the data is ready
       (fail-safe timers at 2s / 2.5s / 4.5s), so a slower rotation would mean
       cards 3 and 4 are never seen. The keyframe percentages are duration
       relative, so the same curve just plays twice as fast. */
    .tour-card {
      position: absolute;
      inset: 0;
      opacity: 0;
      animation: cardCyc 10s ease-in-out infinite;
      /* The cards are shorter than the 420px stage and have uneven heights;
         centring keeps the block from riding at the top and stops the caption
         from jumping between slides. */
      display: flex;
      flex-direction: column;
      justify-content: center;
    }

    @keyframes cardCyc {
      0% { opacity: 0; transform: translateY(20px) scale(0.95); }
      5% { opacity: 1; transform: translateY(0) scale(1); }
      22% { opacity: 1; transform: translateY(0) scale(1); }
      27% { opacity: 0; transform: translateY(-16px) scale(0.97); }
      100% { opacity: 0; }
    }

    .card {
      position: relative;
      background: rgba(255, 255, 255, 0.97);
      border-radius: 18px;
      padding: 24px 24px 34px;
      color: #1e293b;
      box-shadow: 0 30px 60px -15px rgba(15, 23, 42, 0.5);
      text-align: left;
    }

    /* Marks the figures as illustrative — a brand-new account with zero
       properties must not read "12 Biens" as its own data. #510 pt.50 — this
       used to be 10.5px in a light grey (#94a3b8) close to the card's own
       #f1f5ff background: technically present, practically unreadable. A
       pill (background + darker text) reads as a label at a glance instead
       of requiring the eye to hunt for pale small-caps in a corner. */
    .sample-tag {
      position: absolute;
      bottom: 12px;
      right: 20px;
      font-size: 11px;
      font-weight: 800;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: #475569;
      background: #e2e8f0;
      padding: 2px 7px;
      border-radius: 999px;
    }

    .caption {
      margin-top: 20px;
      text-align: center;
      font-size: 18px;
      font-weight: 700;
    }

    .card-head {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 18px;
    }

    .card-title { font-size: 16px; font-weight: 800; }
    .card-title.mb { display: block; margin-bottom: 18px; }

    .badge {
      font-size: 12px;
      font-weight: 700;
      color: #fff;
      background: #7C3BEE;
      padding: 4px 10px;
      border-radius: 999px;
    }

    .task {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 12px;
    }

    .chk {
      width: 22px;
      height: 22px;
      border-radius: 6px;
      border: 2px solid #cbd5e1;
      flex: 0 0 auto;
    }

    .chk-on { border-color: #7C3BEE; }

    .sk { flex: 1; }
    .sk i { display: block; height: 9px; background: #e2e8f0; border-radius: 4px; }
    .sk i.lo { height: 7px; background: #eef1f7; margin-top: 6px; }

    .money {
      font-size: 28px;
      font-weight: 800;
      color: #16a34a;
      margin: 4px 0 18px;
    }

    .money span {
      font-size: 13px;
      font-weight: 600;
      color: #94a3b8;
      margin-left: 8px;
    }

    .chart {
      display: flex;
      align-items: flex-end;
      gap: 10px;
      height: 110px;
    }

    .bar {
      flex: 1;
      border-radius: 6px 6px 0 0;
      transform-origin: bottom;
      background: linear-gradient(#C4B0FA, #7C3BEE);
      animation: barPulse 2.4s ease-in-out infinite;
    }

    .bar.hi { background: linear-gradient(#B79BFF, #9A4BE8); }

    @keyframes barPulse {
      0%, 100% { transform: scaleY(0.82); }
      50% { transform: scaleY(1); }
    }

    .stats {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
    }

    .stat {
      background: #f1f5ff;
      border-radius: 12px;
      padding: 16px;
    }

    .stat b { display: block; font-size: 26px; font-weight: 800; }
    .stat span { font-size: 12.5px; color: #64748b; font-weight: 600; }

    .tenant {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 14px;
    }

    .tenant:last-of-type { margin-bottom: 0; }

    .avatar {
      width: 38px;
      height: 38px;
      border-radius: 50%;
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 800;
      font-size: 14px;
      flex: 0 0 auto;
    }

    .tenant b { display: block; font-size: 14px; font-weight: 700; }
    .tenant i { font-style: normal; font-size: 12px; font-weight: 600; }
    .tenant i.ok { color: #16a34a; }
    .tenant i.warn { color: #f59e0b; }

    /* Below 900px the stage is dropped entirely — the rail alone is still a
       complete, honest loading screen. */
    @media (max-width: 900px) {
      .tour-stage { display: none; }
      .tour-rail { width: 100%; flex: 1; padding: 32px; }
    }

    @media (prefers-reduced-motion: reduce) {
      .tour-card, .bar, .blob { animation: none !important; }
      .tour-card:first-of-type { opacity: 1; }
    }

    /* ─────────────────── Signup-init (original screen) ─────────────────── */

    .loading-container {
      text-align: center;
      color: white;
      max-width: 400px;
      padding: 2rem;
      position: relative;
    }

    .loading-content {
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .loading-subtitle {
      font-size: 0.95rem;
      font-weight: 300;
      color: rgba(255, 255, 255, 0.75);
      margin: 0 0 1.5rem 0;
      letter-spacing: 0.01em;
    }

    .loading-tips {
      margin-top: 1.75rem;
      padding: 0.85rem 1rem;
      display: flex;
      align-items: center;
      gap: 0.6rem;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 8px;
      backdrop-filter: blur(10px);
      min-height: 2.6rem;
    }

    .tip-bulb {
      flex: 0 0 auto;
      font-size: 18px;
      width: 18px;
      height: 18px;
      color: rgba(255, 255, 255, 0.75);
    }

    .tip-text {
      font-size: 0.9rem;
      line-height: 1.4;
      color: rgba(255, 255, 255, 0.9);
      margin: 0;
      text-align: left;
      animation: tipFadeIn 0.5s ease;
    }

    @keyframes tipFadeIn {
      from { opacity: 0; transform: translateY(4px); }
      to   { opacity: 1; transform: translateY(0); }
    }

    /* Only the tour still credits the product at the foot of the screen. The
       signup screen now carries the full lockup, so a second "Proporio" under
       it would say the same thing twice. */
    .mode-tour .powered-by {
      position: absolute;
      bottom: 30px;
      left: 0;
      right: 0;
      text-align: center;
      color: rgba(255, 255, 255, 0.5);
      font-size: 13px;
      font-weight: 600;
      letter-spacing: 0.22em;
      text-transform: uppercase;
    }

    /* The rail's mark is sized by height; the component fits its width to it. */
    .tour-brand__mark {
      display: block;
      height: 34px;
    }

    /* Responsive Design */
    @media (max-width: 480px) {
      .loading-container {
        padding: 1.5rem;
        max-width: 320px;
      }
    }
  `]
})
export class AuthenticatedLoadingScreenComponent {
  private authenticatedLoadingCoordinator = inject(AuthenticatedLoadingCoordinatorService);

  Math = Math;

  private readonly loadingState$: Observable<AuthenticatedLoadingState> =
    this.authenticatedLoadingCoordinator.loadingState$;

  /** Tracks whether we ever mounted revealed chrome this boot (for fade-out). */
  private hadRevealedOverlay = false;

  /**
   * Mounts overlay only when revealed; keeps a 400ms fade frame then unmounts
   * so we never leave a permanent opacity:0 layer in the DOM.
   */
  readonly displayState$: Observable<DisplayState | null> = this.loadingState$.pipe(
    switchMap(state => {
      if (state.isLoading && state.revealOverlay) {
        this.hadRevealedOverlay = true;
        return of({ ...state, fading: false });
      }
      if (state.isLoading && !state.revealOverlay) {
        // Anti-flash window: HTML splash still owns the curtain.
        return of(null);
      }
      // Completed without ever revealing — stay unmounted (auth routes, hot cache).
      if (!this.hadRevealedOverlay) {
        return of(null);
      }
      this.hadRevealedOverlay = false;
      return concat(
        of({ ...state, fading: true }),
        timer(400).pipe(map(() => null as DisplayState | null))
      );
    }),
    distinctUntilChanged((a, b) =>
      a === b ||
      (!!a && !!b &&
        a.isLoading === b.isLoading &&
        a.revealOverlay === b.revealOverlay &&
        a.uiMode === b.uiMode &&
        a.phase === b.phase &&
        a.progress === b.progress &&
        a.message === b.message &&
        a.fading === b.fading &&
        a.markPhase === b.markPhase &&
        a.phaseLabels === b.phaseLabels &&
        a.workspaceSwitch === b.workspaceSwitch)
    )
  );

}
